<template>
  <div class="dashboard-gift-card-sales">
    <MetaManager
        title="Gift Card Sales Analytics"
        description="Your gift card sales performance and analytics"
      />
    <h1>Gift Card Sales Analytics</h1>
    <div class="controls-container">
      <div class="date-range-picker">
        <label for="start-date">Start Date:</label>
        <input type="date" v-model="startDate" id="start-date">
        <label for="end-date">End Date:</label>
        <input type="date" v-model="endDate" id="end-date">
        <button @click="fetchGiftCardData" class="fetch-button">Fetch Data</button>
      </div>
      <div class="filter-container">
        <button @click="exportToExcel" class="export-button">
          <i class="fas fa-file-excel"></i> Export to Excel
        </button>
      </div>
    </div>

    <div class="summary-boxes">
      <div class="summary-box">
        <h2>Total Gift Cards Sold</h2>
        <p>{{ totalGiftCards }}</p>
      </div>
      <div class="summary-box">
        <h2>Total Revenue</h2>
        <p>${{ totalRevenue.toFixed(2) }}</p>
      </div>
      <div class="summary-box">
        <h2>Unique Gift Cards</h2>
        <p>{{ giftCardSales.length }}</p>
      </div>
    </div>

    <div class="analytics-sections">
      <!-- Gift Card Sales Summary -->
      <div class="section">
        <h2>Gift Card Sales Summary</h2>
        <div class="table-controls">
          <div class="search-container">
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search gift cards..."
              @input="applyFilters"
              class="search-input"
            >
          </div>
          <div class="results-info">
            Showing {{ filteredGiftCardSales.length }} of {{ giftCardSales.length }} gift cards
          </div>
        </div>

        <div class="table-container">
          <table class="analytics-table desktop-table">
            <thead>
              <tr>
                <th @click="sortBy('dealTitle')">
                  Gift Card Title
                  <span class="sort-icon" v-if="sortColumn === 'dealTitle'">
                    {{ sortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th @click="sortBy('businessName')">
                  Business
                  <span class="sort-icon" v-if="sortColumn === 'businessName'">
                    {{ sortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th @click="sortBy('unitsSold')">
                  Units Sold
                  <span class="sort-icon" v-if="sortColumn === 'unitsSold'">
                    {{ sortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th @click="sortBy('totalRevenue')">
                  Total Revenue
                  <span class="sort-icon" v-if="sortColumn === 'totalRevenue'">
                    {{ sortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sale in filteredGiftCardSales" :key="sale.dealTitle">
                <td class="deal-title">{{ sale.dealTitle }}</td>
                <td>{{ sale.businessName }}</td>
                <td>{{ sale.unitsSold }}</td>
                <td>${{ sale.totalRevenue.toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>

          <div class="mobile-cards">
            <div v-for="sale in filteredGiftCardSales" :key="sale.dealTitle" class="card">
              <div class="card-header">
                <h3>{{ sale.dealTitle }}</h3>
                <span class="business-name">{{ sale.businessName }}</span>
              </div>
              <div class="card-details">
                <p><strong>Units Sold:</strong> {{ sale.unitsSold }}</p>
                <p><strong>Total Revenue:</strong> ${{ sale.totalRevenue.toFixed(2) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Individual Orders -->
      <div class="section">
        <h2>Individual Gift Card Orders</h2>
        <div class="table-controls">
          <div class="search-container">
            <input
              type="text"
              v-model="orderSearchQuery"
              placeholder="Search orders..."
              @input="applyOrderFilters"
              class="search-input"
            >
          </div>
          <div class="results-info">
            Showing {{ filteredGiftCardOrders.length }} of {{ giftCardOrders.length }} orders
          </div>
        </div>

        <div class="table-container">
          <table class="analytics-table desktop-table">
            <thead>
              <tr>
                <th @click="sortOrdersBy('order_id')">
                  Order ID
                  <span class="sort-icon" v-if="orderSortColumn === 'order_id'">
                    {{ orderSortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th @click="sortOrdersBy('dealTitle')">
                  Gift Card Title
                  <span class="sort-icon" v-if="orderSortColumn === 'dealTitle'">
                    {{ orderSortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th @click="sortOrdersBy('businessName')">
                  Business
                  <span class="sort-icon" v-if="orderSortColumn === 'businessName'">
                    {{ orderSortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th @click="sortOrdersBy('user_name')">
                  Customer
                  <span class="sort-icon" v-if="orderSortColumn === 'user_name'">
                    {{ orderSortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th @click="sortOrdersBy('quantity')">
                  Quantity
                  <span class="sort-icon" v-if="orderSortColumn === 'quantity'">
                    {{ orderSortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th @click="sortOrdersBy('itemRevenue')">
                  Revenue
                  <span class="sort-icon" v-if="orderSortColumn === 'itemRevenue'">
                    {{ orderSortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
                <th @click="sortOrdersBy('created_at')">
                  Date
                  <span class="sort-icon" v-if="orderSortColumn === 'created_at'">
                    {{ orderSortDirection === 'asc' ? '▲' : '▼' }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in filteredGiftCardOrders" :key="`${order.order_id}-${order.dealTitle}`">
                <td>{{ order.order_id }}</td>
                <td class="deal-title">{{ order.dealTitle }}</td>
                <td>{{ order.businessName }}</td>
                <td>{{ order.user_name }}</td>
                <td>{{ order.quantity }}</td>
                <td>${{ order.itemRevenue.toFixed(2) }}</td>
                <td>{{ formatDate(order.created_at) }}</td>
              </tr>
            </tbody>
          </table>

          <div class="mobile-cards">
            <div v-for="order in filteredGiftCardOrders" :key="`${order.order_id}-${order.dealTitle}`" class="card">
              <div class="card-header">
                <h3>Order #{{ order.order_id }}</h3>
                <span class="order-date">{{ formatDate(order.created_at) }}</span>
              </div>
              <div class="card-details">
                <p><strong>Gift Card:</strong> {{ order.dealTitle }}</p>
                <p><strong>Business:</strong> {{ order.businessName }}</p>
                <p><strong>Customer:</strong> {{ order.user_name }}</p>
                <p><strong>Quantity:</strong> {{ order.quantity }}</p>
                <p><strong>Revenue:</strong> ${{ order.itemRevenue.toFixed(2) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import MetaManager from '../../components/MetaManager.vue';

export default {
  name: 'DashboardGiftCardSales',
  components: {
    MetaManager,
  },
  data() {
    return {
      totalGiftCards: 0,
      totalRevenue: 0,
      giftCardSales: [],
      giftCardOrders: [],
      filteredGiftCardSales: [],
      filteredGiftCardOrders: [],
      startDate: '',
      endDate: '',
      searchQuery: '',
      orderSearchQuery: '',
      sortColumn: 'totalRevenue',
      sortDirection: 'desc',
      orderSortColumn: 'created_at',
      orderSortDirection: 'desc',
    };
  },
  methods: {
    setDefaultDateRange() {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      this.startDate = firstDayOfMonth.toISOString().split('T')[0];
      const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      this.endDate = lastDayOfMonth.toISOString().split('T')[0];
    },
    async fetchGiftCardData() {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const params = {
          startDate: this.startDate,
          endDate: this.endDate
        };

        // Add businessId if user is a business (not admin viewing all)
        if (user && user.business_id) {
          params.businessId = user.business_id;
        }

        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/payment/gift-card-analytics`, {
          params
        });

        this.totalGiftCards = response.data.totalGiftCards;
        this.totalRevenue = response.data.totalRevenue;
        this.giftCardSales = response.data.giftCardSales;
        this.giftCardOrders = response.data.giftCardOrders;

        this.applyFilters();
        this.applyOrderFilters();
      } catch (error) {
        console.error('Error fetching gift card analytics:', error);
      }
    },
    applyFilters() {
      let filtered = [...this.giftCardSales];

      // Apply search query if provided
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(sale =>
          sale.dealTitle.toLowerCase().includes(query) ||
          sale.businessName.toLowerCase().includes(query)
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        let valueA, valueB;

        if (['unitsSold', 'totalRevenue'].includes(this.sortColumn)) {
          valueA = parseFloat(a[this.sortColumn]);
          valueB = parseFloat(b[this.sortColumn]);
        } else {
          valueA = a[this.sortColumn];
          valueB = b[this.sortColumn];
        }

        if (this.sortDirection === 'asc') {
          return valueA > valueB ? 1 : -1;
        } else {
          return valueA < valueB ? 1 : -1;
        }
      });

      this.filteredGiftCardSales = filtered;
    },
    applyOrderFilters() {
      let filtered = [...this.giftCardOrders];

      // Apply search query if provided
      if (this.orderSearchQuery.trim()) {
        const query = this.orderSearchQuery.toLowerCase();
        filtered = filtered.filter(order =>
          String(order.order_id).includes(query) ||
          order.dealTitle.toLowerCase().includes(query) ||
          order.businessName.toLowerCase().includes(query) ||
          order.user_name.toLowerCase().includes(query) ||
          order.user_email.toLowerCase().includes(query)
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        let valueA, valueB;

        if (['quantity', 'itemRevenue'].includes(this.orderSortColumn)) {
          valueA = parseFloat(a[this.orderSortColumn]);
          valueB = parseFloat(b[this.orderSortColumn]);
        } else if (this.orderSortColumn === 'created_at') {
          valueA = new Date(a[this.orderSortColumn]);
          valueB = new Date(b[this.orderSortColumn]);
        } else {
          valueA = a[this.orderSortColumn];
          valueB = b[this.orderSortColumn];
        }

        if (this.orderSortDirection === 'asc') {
          return valueA > valueB ? 1 : -1;
        } else {
          return valueA < valueB ? 1 : -1;
        }
      });

      this.filteredGiftCardOrders = filtered;
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
      this.applyFilters();
    },
    sortOrdersBy(column) {
      if (this.orderSortColumn === column) {
        this.orderSortDirection = this.orderSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.orderSortColumn = column;
        this.orderSortDirection = 'asc';
      }
      this.applyOrderFilters();
    },
    formatDate(dateString) {
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    exportToExcel() {
      const headers = [
        'Gift Card Title',
        'Business Name',
        'Units Sold',
        'Total Revenue'
      ];

      const escapeCSV = (field) => {
        if (field === null || field === undefined) {
          return '""';
        }
        const stringField = String(field);
        return `"${stringField.replace(/"/g, '""')}"`;
      };

      const rows = this.filteredGiftCardSales.map(sale => [
        escapeCSV(sale.dealTitle),
        escapeCSV(sale.businessName),
        escapeCSV(sale.unitsSold),
        escapeCSV('$' + sale.totalRevenue.toFixed(2))
      ]);

      const headerRow = headers.map(header => escapeCSV(header)).join(',');
      const dataRows = rows.map(row => row.join(',')).join('\n');
      const csvContent = headerRow + '\n' + dataRows;

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Gift_Card_Sales_${this.startDate}_to_${this.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    orderSearchQuery() {
      this.applyOrderFilters();
    }
  },
  mounted(){
    this.setDefaultDateRange();
    this.fetchGiftCardData();
  }
};
</script>

<style scoped>
.dashboard-gift-card-sales {
  padding: 20px;
  font-family: 'Arial', sans-serif;
  margin-left: 300px;
  background-color: #f9f9f9;
}

h1 {
  text-align: center;
  margin-bottom: 20px;
  color: #333;
  font-weight: 700;
}

.controls-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 25px;
  background-color: #fff;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.date-range-picker, .filter-container {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.date-range-picker label, .filter-container label {
  font-weight: 600;
  color: #4C6B30;
}

.date-range-picker input, .filter-container input {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  transition: border-color 0.2s;
}

.date-range-picker input:focus, .filter-container input:focus {
  border-color: #4C6B30;
  outline: none;
}

.fetch-button, .export-button {
  padding: 8px 15px;
  background-color: #4C6B30;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.fetch-button:hover, .export-button:hover {
  background-color: #6E9F49;
}

.summary-boxes {
  display: flex;
  justify-content: space-between;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 15px;
}

.summary-box {
  flex: 1;
  min-width: 200px;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

.summary-box:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.summary-box h2 {
  font-size: 1.1em;
  margin-bottom: 10px;
  color: #4C6B30;
}

.summary-box p {
  font-size: 1.8em;
  font-weight: 700;
  color: #333;
}

.analytics-sections {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.section {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.section h2 {
  margin-bottom: 20px;
  color: #4C6B30;
  font-size: 1.5em;
}

.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.search-container {
  flex: 1;
  max-width: 300px;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
}

.results-info {
  font-size: 0.9em;
  color: #666;
}

.table-container {
  overflow-x: auto;
}

.analytics-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.analytics-table th,
.analytics-table td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.analytics-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #4C6B30;
  cursor: pointer;
  user-select: none;
  position: relative;
}

.analytics-table th:hover {
  background-color: #e9e9e9;
}

.sort-icon {
  margin-left: 5px;
  color: #4C6B30;
}

.analytics-table tr:hover {
  background-color: #f9f9f9;
}

.deal-title {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-cards {
  display: none;
}

.card {
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.card-header h3 {
  margin: 0;
  color: #4C6B30;
  font-size: 1.1em;
}

.business-name, .order-date {
  font-size: 0.9em;
  color: #666;
}

.card-details p {
  margin: 5px 0;
  display: flex;
  justify-content: space-between;
}

@media(max-width: 1024px){
  .dashboard-admin-gift-card-sales{
    margin-left: 0;
  }

  .controls-container {
    flex-direction: column;
    gap: 15px;
  }

  .date-range-picker, .filter-container {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .dashboard-gift-card-sales {
    margin-left: 0;
    padding: 10px;
  }

  .summary-box {
    min-width: 100%;
  }

  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }

  .table-controls {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .search-container {
    max-width: 100%;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .controls-container, .summary-boxes, .section {
    padding: 10px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
}

@media (max-width: 1200px) {
  .analytics-table th,
  .analytics-table td {
    padding: 8px 6px;
    font-size: 0.9em;
  }
}
</style>